import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Text } from '../../atoms/Text';
import { Button } from '../../atoms/Button';
import { Input } from '../../atoms/Input';
import { Select } from '../../atoms/Select';
import { Checkbox } from '../../atoms/Checkbox';
import { Link } from '../../atoms/Link';
import { Errorbox } from '../../atoms/Errorbox';
import { Icon } from '../../atoms/Icon';
import { Flex } from '../../atoms/Flex';
import { Form } from '../../molecules/Form';
import { FormField } from '../../molecules/FormField';
import { GradientBar } from '../../atoms/GradientBar';
import './RegisterPage.css';

// ─── Constants ───────────────────────────────────────────────────────────────

const FORM_ID = 'register-form';

const DEFAULT_EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'EL',
  'ES',
  'FI',
  'FR',
  'GB',
  'HR',
  'HU',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SE',
  'SI',
  'SK',
];

const TITLE_OPTIONS = ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr (m)', 'Dr (f)'];

/** Fallback phone regex matching HI::Validation::contact_regex->{phone} */
const PHONE_REGEX = /^(?:\+\d{1,3}[.()\s-])?[0-9()\s.-]+$/;

const PHONE_ERROR = 'Please enter a valid phone number (e.g. 01234 567890 or +44 1234 567890).';

/** Fallback email regex */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMAIL_ERROR = 'Please enter a valid email address (e.g. name@example.com).';

/** Map CGI error codes to human-readable messages */
const ERROR_MESSAGES: Record<string, string> = {
  bad_first_name: 'Missing or bad characters in first name.',
  bad_last_name: 'Missing or bad characters in last name.',
  bad_address1: 'Missing or bad characters in first address line.',
  bad_town: 'Missing or bad characters in town.',
  bad_county: 'Missing or bad characters in county.',
  bad_postcode: 'Missing or bad characters in postcode.',
  bad_telephone: 'Missing or bad characters in telephone.',
  bad_mobile: 'Missing or bad characters in mobile.',
  bad_email: 'Missing or bad characters in email.',
  bad_country_code: 'Missing or bad characters in country code.',
  bad_vat_number: 'Missing or bad characters in VAT number.',
  email_nomatch: 'Your email address does not match.',
  bad_year: 'Invalid year in birth date.',
  bad_day: 'Invalid day of month in birth date.',
  bad_date_of_birth: 'Invalid birth date.',
  invalid_vat_number: 'Failed to verify VAT number.',
  pwd_no_match: 'The passwords you entered do not match.',
  pwd_validation: 'The password you entered does not meet our complexity requirements.',
  bad_captcha: 'Please complete the ReCAPTCHA test.',
  internal: 'An internal error has occurred.',
};

const PASSWORD_MESSAGES: Record<string, string> = {
  'TOO SHORT': 'Must be at least 8 characters.',
  'NO DIGIT': 'Password does not contain any numbers.',
  'NO LOWERCASE': 'Password does not contain any lower-case characters.',
  'NO UPPERCASE': 'Password does not contain any upper-case characters.',
  'NO SPECIAL': 'Password does not contain any special characters.',
  'INVALID SPECIAL': 'Password contains an invalid character.',
  GUESSABLE: 'Password contains text that matches some of your personal data: ',
  GENERIC: 'Insecure password, please try again.',
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface CountryOption {
  id: string;
  name: string;
}

export interface RegisterPageProps {
  /** Form action URL */
  action?: string;
  /** ReCAPTCHA v2 site key — container div is rendered, template initialises */
  recaptchaSiteKey?: string;
  /** Password validation AJAX endpoint */
  passwordValidateUrl?: string;
  /** Presales www domain for links */
  wwwDomain: string;
  /** Contact us URL */
  contactUrl?: string;
  /** Password recovery URL */
  recoverPasswordUrl?: string;
  /** Sales email address (shown in IP restriction error) */
  salesEmailAddress?: string;
  /** Field-level validation regex patterns (from HI::Validation::contact_regex) */
  validations?: Record<string, string>;
  /** Country dropdown options */
  countries: CountryOption[];
  /** EU country codes for VAT field visibility */
  euCountries?: string[];
  /** Pre-filled form values (sticky form on error re-render) */
  defaultValues?: {
    title?: string;
    fname?: string;
    lname?: string;
    email?: string;
    dob?: string;
    orgtype?: number;
    country?: string;
    address1?: string;
    address2?: string;
    town?: string;
    postcode?: string;
    county?: string;
    telephone?: string;
    mobile?: string;
    company?: string;
    vatNumber?: string;
    partner?: string;
  };
  /** Server-side error codes from CGI validation */
  errors?: string[];
  /** Override fetch for testing */
  fetchFn?: typeof fetch;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function debounce<T extends (...args: any[]) => void>(fn: T, delay: number): T {
  let timer: ReturnType<typeof setTimeout>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  }) as T;
}

function getPasswordMessage(errors: string[]): string {
  for (const err of errors) {
    if (err.startsWith('GUESSABLE')) {
      const match = err.split(':')[1] || '';
      return PASSWORD_MESSAGES['GUESSABLE'] + match;
    }
    if (PASSWORD_MESSAGES[err]) {
      return PASSWORD_MESSAGES[err];
    }
  }
  return PASSWORD_MESSAGES['GENERIC'];
}

function getFormElement(): HTMLFormElement | null {
  return document.getElementById(FORM_ID) as HTMLFormElement | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const RegisterPage: React.FC<RegisterPageProps> = ({
  action = '/register-free',
  recaptchaSiteKey,
  passwordValidateUrl = '/verify_password.cgi',
  wwwDomain,
  contactUrl,
  recoverPasswordUrl = '/recover.cgi',
  salesEmailAddress,
  validations,
  countries,
  euCountries = DEFAULT_EU_COUNTRIES,
  defaultValues,
  errors: serverErrors,
  fetchFn,
}) => {
  const fetcher = fetchFn || fetch;

  // ── Controlled state ────────────────────────────────────────────────────

  const [orgtype, setOrgtype] = useState<number>(defaultValues?.orgtype ?? 0);
  const [country, setCountry] = useState<string>(defaultValues?.country || 'GB');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState(defaultValues?.email || '');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showAddress, setShowAddress] = useState(
    Boolean(defaultValues?.address1 || defaultValues?.town || defaultValues?.postcode)
  );
  const [address1, setAddress1] = useState(defaultValues?.address1 || '');
  const [address2, setAddress2] = useState(defaultValues?.address2 || '');
  const [town, setTown] = useState(defaultValues?.town || '');
  const [postcode, setPostcode] = useState(defaultValues?.postcode || '');

  // ── Validation state ────────────────────────────────────────────────────

  const [passwordValid, setPasswordValid] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordChecked, setPasswordChecked] = useState(false);
  const [emailConfirmTouched, setEmailConfirmTouched] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [fieldStates, setFieldStates] = useState<Record<string, 'default' | 'error' | 'success'>>(
    {}
  );

  // ── Derived values ──────────────────────────────────────────────────────

  const isOrg = orgtype > 0;
  const euSet = useMemo(() => new Set(euCountries), [euCountries]);
  const showVat = isOrg && euSet.has(country);
  const showCounty = country === 'US' || country === 'CA';
  const emailsMatch = email === emailConfirm;

  // ── Server error rendering ──────────────────────────────────────────────

  const serverErrorContent = useMemo(() => {
    if (!serverErrors?.length) return null;

    const messages: React.ReactNode[] = [];
    const badFields: string[] = [];

    for (const code of serverErrors) {
      if (code === 'email_exists') {
        messages.push(
          <React.Fragment key={code}>
            The email address you have specified already exists in our system. To recover your
            password for this account, <Link href={recoverPasswordUrl}>click here</Link>.
          </React.Fragment>
        );
      } else if (code === 'bad_ip_address') {
        messages.push(
          <React.Fragment key={code}>
            The IP address you are attempting to sign up from is listed as outside of the UK,
            Ireland, Germany or France. To reduce fraud we can only allow automatic signups within
            these countries. To complete the signup process, please email us at{' '}
            {salesEmailAddress ? (
              <Link href={`mailto:${salesEmailAddress}`}>{salesEmailAddress}</Link>
            ) : (
              'our sales team'
            )}
            .
          </React.Fragment>
        );
      } else if (ERROR_MESSAGES[code]) {
        const msg = ERROR_MESSAGES[code];
        if (msg.startsWith('Missing or bad characters in ')) {
          const fieldName = msg.replace('Missing or bad characters in ', '').replace('.', '');
          badFields.push(fieldName);
        } else {
          messages.push(<React.Fragment key={code}>{msg}</React.Fragment>);
        }
      }
    }

    if (badFields.length > 0) {
      messages.unshift(
        <React.Fragment key="bad_fields">
          Missing or bad characters in {badFields.join(', ')}.
        </React.Fragment>
      );
    }

    return messages;
  }, [serverErrors, recoverPasswordUrl, salesEmailAddress]);

  // ── Password validation (debounced AJAX) ────────────────────────────────

  const validatePassword = useMemo(
    () =>
      debounce(async (pwd: string) => {
        // Gather form data for GUESSABLE check
        const customerData: Record<string, string> = {};
        const form = getFormElement() as HTMLFormElement;
        const fd = new FormData(form);
        for (const key of [
          'fname',
          'lname',
          'email',
          'address1',
          'address2',
          'postcode',
          'town',
          'telephone',
          'mobile',
          'company',
          'vat_number',
        ]) {
          const val = fd.get(key);
          if (val) customerData[key] = String(val);
        }

        try {
          const response = await fetcher(passwordValidateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ password: pwd, customer_data: customerData }),
          });
          const data = await response.json();
          const isValid = data.valid === 1;
          setPasswordValid(isValid);
          setPasswordChecked(true);
          setPasswordMessage(isValid ? '' : getPasswordMessage(data.errors || []));
        } catch {
          // On error, assume valid (matches legacy behaviour)
          setPasswordValid(true);
          setPasswordChecked(true);
          setPasswordMessage('');
        }
      }, 100),
    [fetcher, passwordValidateUrl]
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const pwd = e.target.value;
      setPassword(pwd);
      if (pwd.length >= 4) {
        validatePassword(pwd);
      } else {
        setPasswordValid(false);
        setPasswordChecked(false);
        setPasswordMessage('');
      }
    },
    [validatePassword]
  );

  // ── Field-level regex validation ────────────────────────────────────────

  const validateField = useCallback(
    (fieldName: string, value: string) => {
      if (!validations?.[fieldName]) return;
      try {
        const regex = new RegExp(validations[fieldName]);
        const isValid = regex.test(value);
        setFieldStates((prev) => ({ ...prev, [fieldName]: isValid ? 'success' : 'error' }));
      } catch {
        // Invalid regex — skip validation
      }
    },
    [validations]
  );

  const validatePhone = useCallback(
    (value: string) => {
      const regex = validations?.telephone ? new RegExp(validations.telephone) : PHONE_REGEX;
      const isValid = regex.test(value);
      setFieldStates((prev) => ({ ...prev, telephone: isValid ? 'success' : 'error' }));
      setValidationErrors((prev) => {
        if (isValid) {
          const next = { ...prev };
          delete next.telephone;
          return next;
        }
        return { ...prev, telephone: PHONE_ERROR };
      });
    },
    [validations]
  );

  const validateEmail = useCallback(
    (value: string) => {
      const regex = validations?.email ? new RegExp(validations.email) : EMAIL_REGEX;
      const isValid = regex.test(value);
      setFieldStates((prev) => ({ ...prev, email: isValid ? 'success' : 'error' }));
      setValidationErrors((prev) => {
        if (isValid) {
          const next = { ...prev };
          delete next.email;
          return next;
        }
        return { ...prev, email: EMAIL_ERROR };
      });
    },
    [validations]
  );

  const handleFieldBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      if (name && value) {
        if (name === 'telephone') {
          validatePhone(value);
        } else if (name === 'email') {
          validateEmail(value);
        } else {
          validateField(name, value);
        }
      }
    },
    [validateField, validatePhone, validateEmail]
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value) {
        validatePhone(value);
      } else {
        setFieldStates((prev) => ({ ...prev, telephone: 'default' }));
        setValidationErrors((prev) => {
          const next = { ...prev };
          delete next.telephone;
          return next;
        });
      }
    },
    [validatePhone]
  );

  // ── Form submission ─────────────────────────────────────────────────────

  const submitForm = useCallback(() => {
    getFormElement()?.submit();
  }, []);

  const handleSubmit = useCallback(
    (_data: FormData, _event: React.FormEvent<HTMLFormElement>) => {
      const errs: Record<string, string> = {};

      // Show address fields on submit attempt
      setShowAddress(true);

      // Required field checks
      const form = getFormElement() as HTMLFormElement;
      const fd = new FormData(form);
      if (!fd.get('fname')?.toString().trim()) errs.fname = 'Please enter your first name.';
      if (!fd.get('lname')?.toString().trim()) errs.lname = 'Please enter your last name.';
      const emailVal = fd.get('email')?.toString().trim() || '';
      if (!emailVal) {
        errs.email = 'Please enter your email address.';
      } else {
        const emailRegex = validations?.email ? new RegExp(validations.email) : EMAIL_REGEX;
        if (!emailRegex.test(emailVal)) {
          errs.email = EMAIL_ERROR;
        }
      }
      if (!fd.get('dob')?.toString().trim()) errs.dob = 'Please enter your date of birth.';
      const phoneVal = fd.get('telephone')?.toString().trim() || '';
      if (!phoneVal) {
        errs.telephone = 'Please enter your telephone number.';
      } else {
        const phoneRegex = validations?.telephone ? new RegExp(validations.telephone) : PHONE_REGEX;
        if (!phoneRegex.test(phoneVal)) {
          errs.telephone = PHONE_ERROR;
        }
      }
      if (!fd.get('address1')?.toString().trim()) errs.address1 = 'Please enter your address.';
      if (!fd.get('town')?.toString().trim()) errs.town = 'Please enter your town.';
      if (!fd.get('postcode')?.toString().trim()) errs.postcode = 'Please enter your postcode.';

      // Email match
      if (!emailsMatch) {
        errs['email-confirm'] = 'Email addresses must match.';
      }

      // Password
      if (!password) {
        errs.password = 'Please enter a password.';
      } else if (!passwordValid) {
        errs.password = passwordMessage || 'Please enter a valid password.';
      }

      // T&C
      const tandcEl = form.querySelector<HTMLInputElement>('[name="tandc"]');
      if (tandcEl && !tandcEl.checked) {
        errs.tandc = 'Please accept the Terms and Conditions.';
      }

      if (Object.keys(errs).length > 0) {
        setValidationErrors(errs);
        // Set field states to 'error' so inputs show red borders
        setFieldStates((prev) => {
          const next = { ...prev };
          for (const key of Object.keys(errs)) {
            next[key] = 'error';
          }
          return next;
        });
        const firstKey = Object.keys(errs)[0];
        const firstEl = form.querySelector<HTMLElement>(`[name="${firstKey}"]`);
        firstEl?.focus();
        return;
      }

      setValidationErrors({});
      submitForm();
    },
    [emailsMatch, password, passwordValid, passwordMessage, submitForm, validations]
  );

  // ── Effects ─────────────────────────────────────────────────────────────

  // Listen for Ideal Postcodes custom event
  // React 18 batches all setState calls here into one render, so the address
  // fields mount already populated — no DOM timing issues.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as globalThis.CustomEvent).detail;
      if (detail) {
        const { address1 = '', address2 = '', town = '', postcode = '' } = detail;
        setAddress1(address1);
        setAddress2(address2);
        setTown(town);
        setPostcode(postcode);
      }
      setShowAddress(true);
    };
    document.addEventListener('register:show-address', handler);
    return () => document.removeEventListener('register:show-address', handler);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────

  const today = new Date().toISOString().split('T')[0];
  const resolvedContactUrl = contactUrl || `https://${wwwDomain}/contact.html`;

  return (
    <div className="hi-ui-register-page">
      <div className="hi-ui-register-page__content">
        {/* Form */}
        <GradientBar />
        <Form
          id={FORM_ID}
          action={action}
          method="post"
          noValidate
          onSubmit={handleSubmit}
          errors={validationErrors}
          showErrorSummary={false}
          className="hi-ui-register-page__form"
        >
          {/* Header */}
          <div className="hi-ui-register-page__header">
            <Text variant="heading-1">Register with Heart Internet</Text>
            <Flex align="center" gap="sm" className="hi-ui-register-page__help">
              <Text variant="body" inline>
                Need assistance?
              </Text>
              <Button as="a" href={resolvedContactUrl} variant="success" size="small">
                Contact Us
              </Button>
            </Flex>
          </div>

          {/* Server errors */}
          {serverErrorContent && serverErrorContent.length > 0 && (
            <Errorbox className="hi-ui-register-page__server-errors">
              {serverErrorContent.map((msg, i) => (
                <div key={i}>{msg}</div>
              ))}
            </Errorbox>
          )}

          <input type="hidden" name="validate" value="Register" />
          <input type="hidden" name="password_confirm" value={password} />
          {defaultValues?.partner && (
            <input type="hidden" name="partner" value={defaultValues.partner} />
          )}

          {/* Row 1: Title, First Name, Last Name */}
          <Flex gap="md" className="hi-ui-register-page__name-row">
            <FormField
              label="Title"
              labelPosition="hidden"
              className="hi-ui-register-page__title-field"
            >
              <Select name="title" defaultValue={defaultValues?.title || 'Mr'}>
                {TITLE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="First name"
              labelPosition="hidden"
              fullWidth
              error={validationErrors.fname}
            >
              <Input
                name="fname"
                placeholder="First Name"
                maxLength={40}
                defaultValue={defaultValues?.fname}
                state={fieldStates.fname || 'default'}
                onBlur={handleFieldBlur}
                fullWidth
              />
            </FormField>
            <FormField
              label="Last name"
              labelPosition="hidden"
              fullWidth
              error={validationErrors.lname}
            >
              <Input
                name="lname"
                placeholder="Last Name"
                maxLength={40}
                defaultValue={defaultValues?.lname}
                state={fieldStates.lname || 'default'}
                onBlur={handleFieldBlur}
                fullWidth
              />
            </FormField>
          </Flex>

          {/* Row 2: Country + Ideal Postcodes search */}
          <Flex gap="md" className="hi-ui-register-page__address-search-row">
            <FormField
              label="Country"
              labelPosition="hidden"
              className="hi-ui-register-page__country-field"
            >
              <Select
                name="country"
                id="register-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
              >
                {countries.map((c) => (
                  <option key={`${c.id}-${c.name}`} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <div id="register-ideal-wrapper" className="hi-ui-register-page__ideal-wrapper">
              <Input id="register-ideal" placeholder="Start typing address here" fullWidth />
            </div>
            {!showAddress && (
              <button
                type="button"
                className="hi-ui-register-page__manual-address-btn"
                onClick={() => setShowAddress(true)}
              >
                Enter address manually
              </button>
            )}
          </Flex>

          {/* Address fields (hidden until shown) */}
          {showAddress && (
            <div className="hi-ui-register-page__address-fields">
              <FormField
                label="Address line 1"
                labelPosition="hidden"
                fullWidth
                error={validationErrors.address1}
              >
                <Input
                  name="address1"
                  id="register-address1"
                  placeholder="Address Line 1"
                  maxLength={80}
                  value={address1}
                  onChange={(e) => setAddress1(e.target.value)}
                  state={fieldStates.address1 || 'default'}
                  onBlur={handleFieldBlur}
                  fullWidth
                />
              </FormField>
              <FormField label="Address line 2" labelPosition="hidden" fullWidth>
                <Input
                  name="address2"
                  id="register-address2"
                  placeholder="Address Line 2"
                  maxLength={80}
                  value={address2}
                  onChange={(e) => setAddress2(e.target.value)}
                  state={fieldStates.address2 || 'default'}
                  onBlur={handleFieldBlur}
                  fullWidth
                />
              </FormField>
              <FormField
                label="Town"
                labelPosition="hidden"
                fullWidth
                error={validationErrors.town}
              >
                <Input
                  name="town"
                  id="register-town"
                  placeholder="Town"
                  maxLength={40}
                  value={town}
                  onChange={(e) => setTown(e.target.value)}
                  state={fieldStates.town || 'default'}
                  onBlur={handleFieldBlur}
                  fullWidth
                />
              </FormField>
              <FormField
                label="Postcode"
                labelPosition="hidden"
                fullWidth
                error={validationErrors.postcode}
              >
                <Input
                  name="postcode"
                  id="register-postcode"
                  placeholder="Postcode"
                  maxLength={40}
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  state={fieldStates.postcode || 'default'}
                  onBlur={handleFieldBlur}
                  fullWidth
                />
              </FormField>
              {showCounty && (
                <FormField label="State / Province" labelPosition="hidden" fullWidth>
                  <Input
                    name="county"
                    id="register-county"
                    placeholder="State / Province"
                    defaultValue={defaultValues?.county}
                    fullWidth
                  />
                </FormField>
              )}
            </div>
          )}

          {/* Row 3: Email + Email Confirm */}
          <Flex gap="md" className="hi-ui-register-page__email-row">
            <FormField
              label="Email"
              labelPosition="hidden"
              fullWidth
              error={validationErrors.email}
            >
              <Input
                type="email"
                name="email"
                placeholder="Email"
                maxLength={200}
                value={email}
                onChange={(e) => {
                  const val = e.target.value;
                  setEmail(val);
                  if (val) {
                    validateEmail(val);
                  } else {
                    setFieldStates((prev) => ({ ...prev, email: 'default' }));
                    setValidationErrors((prev) => {
                      const next = { ...prev };
                      delete next.email;
                      return next;
                    });
                  }
                }}
                state={fieldStates.email || 'default'}
                onBlur={handleFieldBlur}
                fullWidth
              />
            </FormField>
            <FormField
              label="Confirm email"
              labelPosition="hidden"
              fullWidth
              error={validationErrors['email-confirm']}
              success={
                emailConfirmTouched && emailsMatch && emailConfirm ? 'Emails match.' : undefined
              }
            >
              <Input
                type="email"
                name="email-confirm"
                placeholder="Confirm Email"
                maxLength={200}
                value={emailConfirm}
                onChange={(e) => setEmailConfirm(e.target.value)}
                onBlur={() => setEmailConfirmTouched(true)}
                state={
                  emailConfirmTouched && emailConfirm
                    ? emailsMatch
                      ? 'success'
                      : 'error'
                    : 'default'
                }
                fullWidth
              />
            </FormField>
          </Flex>

          {/* Row 4: Password + Telephone */}
          <Flex gap="md" className="hi-ui-register-page__password-row">
            <FormField
              label="Password"
              labelPosition="hidden"
              fullWidth
              error={
                validationErrors.password ||
                (passwordChecked && !passwordValid ? passwordMessage : undefined)
              }
              success={passwordChecked && passwordValid ? 'Password is strong.' : undefined}
            >
              <div className="hi-ui-register-page__password-wrapper">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={handlePasswordChange}
                  state={passwordChecked ? (passwordValid ? 'success' : 'error') : 'default'}
                  fullWidth
                />
                <button
                  type="button"
                  className="hi-ui-register-page__password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'eye-open' : 'eye-closed'} size={18} />
                </button>
              </div>
            </FormField>
            <FormField
              label="Phone"
              labelPosition="hidden"
              fullWidth
              error={validationErrors.telephone}
            >
              <Input
                type="tel"
                name="telephone"
                id="register-telephone"
                placeholder="Phone"
                maxLength={40}
                defaultValue={defaultValues?.telephone}
                state={fieldStates.telephone || 'default'}
                onBlur={handleFieldBlur}
                onChange={handlePhoneChange}
                fullWidth
              />
            </FormField>
          </Flex>

          {/* Row 5: DOB + Org Type */}
          <Flex gap="md" className="hi-ui-register-page__dob-row">
            <FormField label="Date of Birth:" fullWidth error={validationErrors.dob}>
              <Input
                type="date"
                name="dob"
                id="register-dob"
                max={today}
                defaultValue={defaultValues?.dob}
                fullWidth
              />
            </FormField>
            <FormField label="Organisation Type:" fullWidth>
              <Select
                name="orgtype"
                id="register-orgtype"
                value={String(orgtype)}
                onChange={(e) => setOrgtype(Number(e.target.value))}
                fullWidth
              >
                <option value="0">Individual</option>
                <option value="1">Company</option>
                <option value="2">Nonprofit organisation</option>
              </Select>
            </FormField>
          </Flex>

          {/* Row 6: Company + VAT (conditional) */}
          {isOrg && (
            <Flex gap="md" className="hi-ui-register-page__company-row">
              <FormField label="Company name" labelPosition="hidden" fullWidth>
                <Input
                  name="company"
                  id="register-company"
                  placeholder="Company Name"
                  maxLength={80}
                  defaultValue={defaultValues?.company}
                  state={fieldStates.company || 'default'}
                  onBlur={handleFieldBlur}
                  fullWidth
                />
              </FormField>
              {showVat && (
                <FormField label="VAT number" labelPosition="hidden" fullWidth>
                  <Input
                    name="vat_number"
                    id="register-vat-number"
                    placeholder="VAT Number (if applicable)"
                    maxLength={40}
                    defaultValue={defaultValues?.vatNumber}
                    state={fieldStates.vat_number || 'default'}
                    onBlur={handleFieldBlur}
                    fullWidth
                  />
                </FormField>
              )}
            </Flex>
          )}

          {/* Checkboxes */}
          <div className="hi-ui-register-page__checkboxes">
            <Checkbox name="mailme" value="Y">
              I want to receive personalised offers, updates and information about Heart Internet
              products and services
            </Checkbox>
          </div>

          <div className="hi-ui-register-page__checkboxes">
            <Checkbox name="tandc" state={validationErrors.tandc ? 'error' : 'default'}>
              I accept the{' '}
              <Link href={`https://${wwwDomain}/terms`} external>
                Terms and Conditions
              </Link>{' '}
              and{' '}
              <Link href={`https://${wwwDomain}/terms/heart-internet-privacy-statement`} external>
                Privacy Policy
              </Link>
            </Checkbox>
            {validationErrors.tandc && (
              <Text variant="error" className="hi-ui-register-page__tandc-error">
                {validationErrors.tandc}
              </Text>
            )}
          </div>

          {/* Submit row — ReCAPTCHA and button side by side */}
          <div className="hi-ui-register-page__submit-row">
            {recaptchaSiteKey && (
              <div className="hi-ui-register-page__recaptcha">
                <div id="register-recaptcha" />
              </div>
            )}
            <div className="hi-ui-register-page__actions">
              <Button type="submit" variant="primary" size="large">
                Register
              </Button>
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};
